/**
 * Theme Context — dark / light mode switching.
 *
 * The active theme is applied as `data-theme` on <html>; all design tokens
 * in src/index.css resolve through CSS variables scoped to that attribute.
 * index.html sets the dark theme before first paint.
 *
 * Dark only (owner decision, 4 Oct 2026; see ./themePolicy.ts): the stored
 * `app-theme` preference is read but never written on mount and never
 * removed; only an explicit setTheme / toggleTheme call persists it.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { invalidateCache } from '../styles/cssVars';
import { DARK_ONLY, displayedTheme, persistPreference, readPreference, type Theme } from './themePolicy';

export type { Theme } from './themePolicy';

const THEME_COLOR: Record<Theme, string> = {
  dark: '#090e17',
  light: '#f3f5f8',
};

/** A getter, not the global itself: reading `localStorage` throws when storage is blocked. */
const browserStorage = () => window.localStorage;

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
  // Chart/canvas color bridge caches getComputedStyle — drop it on switch.
  invalidateCache();
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  setTheme: () => {},
  toggleTheme: () => {},
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<Theme>(() => readPreference(browserStorage));
  const theme = displayedTheme(preference, DARK_ONLY);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setPreference(t);
    persistPreference(browserStorage, t);
  }, []);
  const toggleTheme = useCallback(() => {
    const next: Theme = preference === 'dark' ? 'light' : 'dark';
    setPreference(next);
    persistPreference(browserStorage, next);
  }, [preference]);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
